import multiFileOverwriteDialogTemplate from './multi-file-overwrite-dialog.tpl.html';
import './multi-file-overwrite-dialog.less';
import { modalComponentModule } from '../modal/modal';
import { cleanPath } from '../../utils/file-utils/file-utils';
import { identity } from '../../utils/function-utils';
import { compareIgnoreCase } from '../../utils/string-utils';

export const multiFileOverwriteDialogComponentModule = angular
  .module('Q.components.multi-file-overwrite-dialog', [modalComponentModule])

  .factory('multiFileOverwriteDialog', function ($modal, $q) {
    function getDuplicates(paths, files) {
      const filePaths = files
        .filter((f) => !f.directory)
        .map((f) => f.path.toLowerCase());
      const restrictedDirPaths = files
        .filter((f) => f.directoryLocked)
        .map((f) => f.path.toLowerCase());

      return (
        paths
          .map(cleanPath)
          .sort(compareIgnoreCase)
          .map((path) => {
            const props = {
              checked: false,
              path,
            };

            const pathLower = path.toLowerCase();

            // Check for paths that would attempt to convert a file into a directory
            const conflictDir = filePaths.some((fp) =>
              pathLower.startsWith(fp + '/'),
            );
            if (conflictDir) {
              props.isLocked = true;
              props.lockedReason = `overwrites file "${conflictDir} with a directory`;
              return props;
            }

            // Check for paths that would modify a restricted directory
            const restrictedDir = restrictedDirPaths.find((fp) =>
              pathLower.startsWith(fp + '/'),
            );
            if (restrictedDir) {
              props.isLocked = true;
              props.lockedReason = `"${restrictedDir}" is restricted`;
              return props;
            }

            // Check for paths that are a duplicate
            const duplicate = files.find(
              (f) => f.path.toLowerCase() === pathLower,
            );
            if (duplicate) {
              props.isDuplicate = true;

              // see if we can modify this file
              let lockedReason = null;
              if (duplicate.readOnly) {
                lockedReason = 'original file is read-only';
              } else if (duplicate.disabled) {
                lockedReason = 'original file is locked';
              } else if (duplicate.directory) {
                lockedReason = 'file would overwrite a directory';
              }

              props.isLocked = !!lockedReason;
              props.lockedReason = lockedReason;
              props.caseChange = !duplicate.locked && duplicate.path !== path;
              return props;
            }

            // this will get filtered out
            return false;
          })

          // Remove any empty results
          .filter(identity)
      );
    }

    return {
      /**
       *
       * @param params.paths {Array<string>} Paths to check
       * @param params.files {Array<{path:string}>} Files to compare against
       * @param params.referencePaths {Array<string>?} Relative Paths to check
       * @param params.referenceFiles {Array<{path:string}>?} Files to compare relative paths against
       * @returns {Promise<Array<string>>} Resolves with an array of paths to keep, rejects if canceled
       */
      open: (params) => {
        const duplicatePaths = getDuplicates(params.paths, params.files);
        const duplicateRefPaths = params.referencePaths
          ? getDuplicates(params.referencePaths, params.referenceFiles)
          : [];

        if (duplicatePaths.length || duplicateRefPaths.length) {
          return $modal
            .openOnce('multiFileOverwriteDialog', {
              template: multiFileOverwriteDialogTemplate,
              controller: 'MultiFileOverwriteDialogCtrl as $ctrl',
              windowClass: 'multi-file-overwrite-dialog',
              resolve: {
                paths: () => duplicatePaths,
                refPaths: () => duplicateRefPaths,
              },
            })
            .result.then(
              ({ paths: excludePaths, refPaths: excludedRefPaths }) => {
                // return all paths, minus the duplicate paths that weren't included
                return {
                  paths: params.paths.filter((p) => !excludePaths.includes(p)),
                  referencePaths: (params.referencePaths || []).filter(
                    (p) => !excludedRefPaths.includes(p),
                  ),
                };
              },
            );
        } else {
          // everything is OK
          return $q.resolve({
            paths: params.paths,
            referencePaths: params.referencePaths,
          });
        }
      },
    };
  })

  .controller(
    'MultiFileOverwriteDialogCtrl',
    function MultiFileOverwriteDialogCtrl($modalInstance, paths, refPaths) {
      const ctrl = this;

      ctrl.paths = paths;
      ctrl.refPaths = refPaths;
      ctrl.cancel = () => {
        // reject dialog
        $modalInstance.dismiss('cancel');
      };
      ctrl.overwriteAll = () => {
        // close dialog with just conflict paths
        $modalInstance.close({
          paths: ctrl.paths.filter((p) => p.isLocked).map((p) => p.path),
          refPaths: ctrl.refPaths.filter((p) => p.isLocked).map((p) => p.path),
        });
      };
      ctrl.continue = () => {
        // close dialog with all excluded or conflict paths
        $modalInstance.close({
          paths: ctrl.paths
            .filter((p) => !p.checked || p.isLocked)
            .map((p) => p.path),
          refPaths: ctrl.refPaths
            .filter((p) => !p.checked || p.isLocked)
            .map((p) => p.path),
        });
      };
    },
  );
